import { Component, OnInit, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogConfig } from '@angular/material/dialog';
import { SelectionModel } from '@angular/cdk/collections';
import { Observable, asapScheduler, firstValueFrom, scheduled } from 'rxjs';

import { AzavistaApiService, IDependency, IStage, IDependencyV2, IAdvancedSearchRequest } from '@azavista/servicelib';
import {
    IField, IRowAction, IPagingOptions, AzavistaSharedService, ITableSettingsChangedData,
    IAdvancedSearchData
} from '@azavista/components/shared';
import { AzavistaFilterCriteriaTreeService, FilterCriteriaTreeDialogData, FilterCriteriaTreeDialogResult } from '@azavista/components/filter-criteria-tree';
import { IRowActionSelectedData } from '@azavista/components/table';
import { DependenciesPopupService } from './dependencies-popup.service';
import { IDependenciesPopupComponentData, ICalculatedDependencyRow } from './interfaces';
import { DependencyRowActionId } from './enums';
import { EventContentWebsiteService, filterTreeToDependencyV2 } from '../../event/website/event-content-website.service';
import { DependenciesService } from 'src/app/event/website/dependencies/dependencies.service';
import { SharedService } from 'src/app/shared/shared.service';
import { RequiredScopesService } from 'src/app/shared/required-scopes.service';
import { AclService } from 'src/app/shared/acl.service';
import { SettingEntityType, TranslationKey } from 'src/app/shared/enums';
import { SettingsService } from 'src/app/shared';

@Component({
    templateUrl: 'dependencies-popup.component.html',
    standalone: false
})
export class DependenciesPopupComponent implements OnInit {
    columns: IField[];
    displayedColumns: string[];
    dataSource: ICalculatedDependencyRow[];
    dependencies: IDependency[];
    actions: IRowAction[];
    selection = new SelectionModel<string>(true);
    pagingOptions: IPagingOptions;
    lastTableSettingsData: ITableSettingsChangedData;
    allDependencyFields$: Observable<IField[]>;
    advancedSearchData?: IAdvancedSearchData;
    private readonly advancedSearchEntity = SettingEntityType.eventDependency;

    constructor(
        private depPopupSvc: DependenciesPopupService, private sharedSvc: SharedService,
        private depsSvc: DependenciesService,
        private apiSvc: AzavistaApiService, private dialogRef: MatDialogRef<DependenciesPopupComponent>,
        private evtContentSvc: EventContentWebsiteService, private cmpSharedSvc: AzavistaSharedService,
        private filterTreeSvc: AzavistaFilterCriteriaTreeService, @Inject(MAT_DIALOG_DATA) private data: IDependenciesPopupComponentData,
        private settingsSvc: SettingsService,
        private rsSvc: RequiredScopesService, private aclSvc: AclService
    ) {
    }

    async ngOnInit(): Promise<void> {
        this.columns = this.depPopupSvc.getDependenciesColumns();
        this.displayedColumns = this.depPopupSvc.getDependenciesDisplayedColumns();
        this.pagingOptions = this.sharedSvc.createDefaultPagingOptionsForLongLists();
        this.actions = this.depPopupSvc.getDependencyActions();
        this.dataSource = [];
        this.dependencies = [];
        this.advancedSearchData = {
            advancedFilters: await this.settingsSvc.getGlobalAdvancedSearchFilters(this.advancedSearchEntity),
            allFields: [ this.depsSvc.getDependencyFieldNameField() ]
        };
        this.lastTableSettingsData = this.sharedSvc.createDefaultTableSettingsChangedData();
        this.allDependencyFields$ = scheduled(this.depsSvc.getDependencyFields(this.data.event), asapScheduler);
        this.searchDependencies(this.lastTableSettingsData);
    }

    async getAllEventParicipantFieldsAndStageId(allStages: IStage[]): Promise<IField[]> {
        const allEventParticipantFields = await this.apiSvc.getAllEventParticipantFields(this.data.event.id);
        this.sharedSvc.addParticipantStageIdField(allEventParticipantFields, allStages);
        return allEventParticipantFields;
    }

    async searchDependencies(data: ITableSettingsChangedData, skipSavingSettings?: boolean): Promise<void> {
        this.lastTableSettingsData = data;
        if (!skipSavingSettings) {
            this.settingsSvc.saveGlobalAdvancedSearchFilters(data, this.advancedSearchData, this.advancedSearchEntity);
        }
        const req: IAdvancedSearchRequest<keyof IDependency> = this.sharedSvc.createAdvancedSearchRequest(data);
        const res = await this.apiSvc.searchDependencies(this.data.event.id, req);
        this.dependencies = res.dependencies;
        this.dataSource = this.depPopupSvc.convertToCalculatedDependencyRows(res.dependencies);
        this.pagingOptions = this.sharedSvc.createPagingOptionsFromPagedResponse(res);
    }

    onRowActionSelected(data: IRowActionSelectedData): void {
        const actionId = data.actionItem.id;
        const ids = data.selectedRows as string[];
        if (actionId === DependencyRowActionId.viewOrEdit) {
            this.processViewOrEditAction(ids[0]);
        } else if (actionId === DependencyRowActionId.delete) {
            this.processDeleteAction(ids[0]);
        }
    }

    async processViewOrEditAction(id: string): Promise<void> {
        const dependency = this.dependencies.find(x => x.id === id);
        const allDependencyFields = await firstValueFrom(this.allDependencyFields$);
        const dialogData = this.evtContentSvc.createEventDependenciesDialogData(dependency, allDependencyFields, false);
        let dialogResult = await this.filterTreeSvc.open({
            ...dialogData,
            canDelete: this.aclSvc.getAclObjectForCurrentUser(this.rsSvc.getRequiredDeleteEventWebsiteDependenciesScopes())?.allowed,
        }, this.getDialogOptions());
        this.handleDependencyEditResult(id, dialogResult);
    }

    async handleDependencyEditResult(dependencyId: string, dialogResult: FilterCriteriaTreeDialogResult<2, true>) {
        if (dialogResult.type === 'delete') {
            const isConfirmed = await this.processDeleteAction(dependencyId);
            if (!isConfirmed) {
                const reopenResult = await this.filterTreeSvc.reopenLastDialog<2, true>(this.getDialogOptions());
                this.handleDependencyEditResult(dependencyId, reopenResult);
            }
        };
        if (dialogResult.type === 'submit') {
            this.processFilterSaved(
                filterTreeToDependencyV2(dialogResult.data, dependencyId)
            );
            return;
        };
    }

    async onAddNew(): Promise<void> {
        const allDependencyFields = await firstValueFrom(this.allDependencyFields$);
        const allFieldsConverted = this.cmpSharedSvc.convertFieldsForAdvancedFilter(allDependencyFields);
        allFieldsConverted.forEach(x => x.editable = true);
        const uniqueId = '' + Date.now(); // all entities must have unique id
        const dialogData: FilterCriteriaTreeDialogData<2, true> = {
            ...this.evtContentSvc.createEventDependenciesDialogData({
                criteria: [],
                name: '',
                id: uniqueId
            }, allFieldsConverted, true),
            dialogTitle: this.sharedSvc.translate(TranslationKey.addNewDependencyFilters),
            canDelete: false
        };
        const dialogResult = await this.filterTreeSvc.open(dialogData, this.getDialogOptions());
        if (dialogResult.type === 'submit') {
            this.processFilterCreated(filterTreeToDependencyV2(dialogResult.data, uniqueId));
        };
    }

    async processFilterCreated(dependency: IDependencyV2): Promise<void> {
        await this.apiSvc.createDependency({
            eventId: this.data.event.id,
            dependency,
        });
        this.loadDependencies();
    }

    /**
     * @returns boolean whether the delete is confirmed
     */
    async processDeleteAction(id: string): Promise<boolean> {
        const confirmationResult = await this.evtContentSvc.confirmDelete();
        if (!confirmationResult) {
            return false;
        }
        await this.apiSvc.deleteDependency(id);
        this.selection.clear();
        this.loadDependencies();
        return true;
    }

    async processFilterSaved(dependency: IDependencyV2): Promise<void> {
        await this.apiSvc.updateDependency(
            dependency
        );
        this.loadDependencies();
    }

    loadDependencies(): void {
        this.searchDependencies(this.lastTableSettingsData);
    }

    onClose(): void {
        this.dialogRef.close();
    }

    private getDialogOptions(): MatDialogConfig<any> {
        return {
            minWidth: '45vw',
            panelClass: 'filter-criteria-tree-dialog',
        };
    }
}
